/* eslint-disable react-hooks/exhaustive-deps */
import _ from 'lodash'
import React, {
  forwardRef,
  useEffect,
  useMemo,
  useState,
  useImperativeHandle,
} from 'react'
import { useTranslation } from 'react-i18next'
import { useSystemSettings, useSmsToken, useEmailToken } from '../../vendor/react-omnitech-api'
import getWhatsappTokenRedirectUrl from '../../helpers/get-whatsapp-token-redirect-url'
import { useAlert } from '../../hook/use-alert'
import useI18n from '../../hook/use-i18n'
import TokenButtonView from './token-button-view'

function TokenButtonController(props, ref) {
  const { currentLocale } = useI18n()
  const alert = useAlert()
  const { getSystemSetting } = useSystemSettings()
  const { t } = useTranslation()
  const smsResendTokenInterval = getSystemSetting('account.sms_token.resend_interval')
  const emailResendTokenInterval = getSystemSetting('account.email_token.resend_interval')
  const siteUrl = getSystemSetting('frontend.site_url')
  const {
    getWhatsAppToken,
    sendSmsToken,
    smsTokenCountDown,
    smsTokenDisable,
    resetCounterSmsToken,
  } = useSmsToken(smsResendTokenInterval)
  const {
    sendEmailToken,
    emailTokenCountDown,
    emailTokenDisable,
    resetCounterEmailToken,
  } = useEmailToken(emailResendTokenInterval)

  const {
    actionType,
    phone,
    useWhatsApp = false,
    whatsappCallbackState = {},
    registrationValues,
    email,
    onStartRequestToken,
    onSuccess,
    onError,
    ...rest
  } = props

  const [loading, setLoading] = useState(false)
  const [tokenData, setTokenData] = useState(null)

  const type = useMemo(() => (
    !_.isEmpty(phone) ? 'phone' : 'email'
  ), [phone])

  const qrCodeValue = useMemo(() => (
    _.get(tokenData, 'redirectUrl')
  ), [tokenData])

  const requestToken = async () => {
    let result
    try {
      setLoading(true)
      if (!_.isEmpty(phone)) {
        if (useWhatsApp) {
          const { whatsappToken } = await getWhatsAppToken(phone)
          const redirectUrl = getWhatsappTokenRedirectUrl({
            ...whatsappToken,
            userPhone: phone,
            actionType,
            registrationValues,
            getMessage: ({ callbackUrl }) => t('ui.whatsappTokenButton.whatsappMessage', { callbackUrl }),
            origin: siteUrl,
            locale: _.get(currentLocale, 'locale'),
            callbackState: whatsappCallbackState,
          })
          result = {
            ...whatsappToken,
            redirectUrl,
          }
        } else {
          result = await sendSmsToken(phone)
        }
      } else if (!_.isEmpty(email)) {
        result = await sendEmailToken(email)
      }
      setTokenData(result)
      return result
    } catch (error) {
      if (_.isFunction(onError)) onError(error)
      const generalError = _.get(error, 'generalError', {})
      alert.show(generalError.message, { state: 'error' })
    } finally {
      setLoading(false)
    }
  }

  useImperativeHandle(ref, () => ({
    resetCounter: !_.isEmpty(email) ? resetCounterEmailToken : resetCounterSmsToken,
  }))

  useEffect(() => (() => {
    resetCounterSmsToken()
    resetCounterEmailToken()
  }), [])

  const onClick = async () => {
    if (
      _.isEmpty(phone)
      && _.isEmpty(email)
    ) return
    if (_.isFunction(onStartRequestToken)) {
      onStartRequestToken()
    }
    requestToken({
      useQrCode: useWhatsApp,
    })
  }

  const onContinue = () => {
    if (qrCodeValue) {
      window.open(qrCodeValue)
    } else {
      onDismiss()
    }
  }

  const onDismiss = () => {
    setTokenData(null)
  }

  useEffect(() => {
    if (
      !_.isEmpty(tokenData)
      && _.isFunction(onSuccess)
    ) {
      onSuccess(tokenData)
    }
  }, [tokenData])

  const viewProps = {
    countDown: type === 'phone' ? smsTokenCountDown : emailTokenCountDown,
    countDownInProgress: type === 'phone' ? smsTokenDisable : emailTokenDisable,
    loading,
    qrCodeValue,
    type: type === 'phone' && useWhatsApp ? 'whatsapp' : type,
    onClick,
    onContinue,
    onDismiss,
    ...rest,
  }

  return (
    <TokenButtonView {...viewProps} />
  )
}

export default forwardRef(TokenButtonController)
